<script setup>
import {
  NCard, NSpace, NH1, NH2, NH3, NP, NInput, NButton, 
  NGradientText, NGrid, NGridItem, NCollapse, NCollapseItem,
  NTabs, NTabPane, NAlert, NTag
} from 'naive-ui';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Search, BookOpen, MessageCircle, Phone, Video,
  Users, Mail, Scale, CreditCard, Shield, Settings,
  HelpCircle, FileText, Star, Clock, Languages,
  Calendar, CheckCircle
} from 'lucide-vue-next';
import { dt } from '@/utils/i18n';

const { t, locale } = useI18n();

const searchQuery = ref('');

// Help categories specific to legal directory
const helpCategories = [
  {
    icon: Users,
    title: 'help.categories.gettingStarted.title',
    description: 'help.categories.gettingStarted.description',
    color: '#36d1dc',
    articles: 6
  },
  {
    icon: Search,
    title: 'help.categories.findingLawyers.title',
    description: 'help.categories.findingLawyers.description',
    color: '#5b86e5',
    articles: 8
  },
  {
    icon: Phone,
    title: 'help.categories.contact.title', 
    description: 'help.categories.contact.description',
    color: '#ef473a',
    articles: 5
  },
  {
    icon: Scale,
    title: 'help.categories.verification.title',
    description: 'help.categories.verification.description',
    color: '#f7b733',
    articles: 4
  },
  {
    icon: Languages,
    title: 'help.categories.languages.title',
    description: 'help.categories.languages.description',
    color: '#11998e',
    articles: 7
  },
  {
    icon: Settings,
    title: 'help.categories.account.title',
    description: 'help.categories.account.description',
    color: '#9b59b6',
    articles: 9
  }
];

// Popular articles
const popularArticles = [
  {
    title: 'help.popular.article1.title',
    category: 'Finding Lawyers',
    readTime: 3
  },
  {
    title: 'help.popular.article2.title', 
    category: 'Getting Started',
    readTime: 5
  },
  {
    title: 'help.popular.article3.title',
    category: 'Contact',
    readTime: 4
  },
  {
    title: 'help.popular.article4.title',
    category: 'Verification',
    readTime: 2
  },
  {
    title: 'help.popular.article5.title',
    category: 'Languages',
    readTime: 6
  }
];

// FAQ by category
const faqByCategory = {
  general: [
    { q: 'help.faq.general.q1.question', a: 'help.faq.general.q1.answer' },
    { q: 'help.faq.general.q2.question', a: 'help.faq.general.q2.answer' },
    { q: 'help.faq.general.q3.question', a: 'help.faq.general.q3.answer' },
    { q: 'help.faq.general.q4.question', a: 'help.faq.general.q4.answer' }
  ],
  lawyers: [
    { q: 'help.faq.lawyers.q1.question', a: 'help.faq.lawyers.q1.answer' },
    { q: 'help.faq.lawyers.q2.question', a: 'help.faq.lawyers.q2.answer' },
    { q: 'help.faq.lawyers.q3.question', a: 'help.faq.lawyers.q3.answer' },
    { q: 'help.faq.lawyers.q4.question', a: 'help.faq.lawyers.q4.answer' }
  ],
  contact: [
    { q: 'help.faq.contact.q1.question', a: 'help.faq.contact.q1.answer' },
    { q: 'help.faq.contact.q2.question', a: 'help.faq.contact.q2.answer' },
    { q: 'help.faq.contact.q3.question', a: 'help.faq.contact.q3.answer' },
    { q: 'help.faq.contact.q4.question', a: 'help.faq.contact.q4.answer' }
  ],
  technical: [
    { q: 'help.faq.technical.q1.question', a: 'help.faq.technical.q1.answer' },
    { q: 'help.faq.technical.q2.question', a: 'help.faq.technical.q2.answer' },
    { q: 'help.faq.technical.q3.question', a: 'help.faq.technical.q3.answer' },
    { q: 'help.faq.technical.q4.question', a: 'help.faq.technical.q4.answer' }
  ]
};

// Contact options
const contactOptions = [
  {
    icon: MessageCircle,
    title: 'help.contact.chat.title',
    description: 'help.contact.chat.description',
    action: 'help.contact.chat.action',
    color: '#36d1dc'
  },
  {
    icon: Mail,
    title: 'help.contact.email.title',
    description: 'help.contact.email.description', 
    action: 'help.contact.email.action',
    color: '#5b86e5'
  },
  {
    icon: HelpCircle,
    title: 'help.contact.faq.title',
    description: 'help.contact.faq.description',
    action: 'help.contact.faq.action',
    color: '#ef473a'
  }
];
</script>

<template>
  <div class="help-container">
    <!-- Hero Section with Search -->
    <div class="hero-section">
      <NH1 class="hero-title">
        <NGradientText :gradient="{ from: '#36d1dc', to: '#5b86e5', deg: 45 }">
          {{ dt('help.heroTitle') }}
        </NGradientText>
      </NH1>
      <NP class="hero-subtitle">{{ dt('help.heroSubtitle') }}</NP>
      
      <div class="search-section">
        <NInput
          v-model:value="searchQuery"
          size="large"
          :placeholder="dt('help.search.placeholder')"
          class="search-input"
        >
          <template #prefix>
            <Search size="20" />
          </template>
          <template #suffix>
            <NButton type="primary" ghost>
              {{ dt('help.search.button') }}
            </NButton>
          </template>
        </NInput>
        <div class="search-suggestions">
          <span class="suggestion-label">{{ dt('help.search.suggestions') }}</span>
          <NTag 
            v-for="suggestion in ['help.search.suggestion1', 'help.search.suggestion2', 'help.search.suggestion3']"
            :key="suggestion" 
            class="suggestion-tag"
            @click="searchQuery = dt(suggestion)"
          >
            {{ dt(suggestion) }}
          </NTag>
        </div>
      </div>
    </div>

    <!-- Help Categories -->
    <div class="categories-section">
      <NH2 class="section-title">{{ dt('help.categoriesTitle') }}</NH2>
      <NGrid :cols="3" :x-gap="20" :y-gap="20" responsive="screen" :collapsed="['xs', 's']">
        <NGridItem v-for="(category, index) in helpCategories" :key="index">
          <div class="category-card">
            <div class="category-icon" :style="{ backgroundColor: category.color }">
              <component :is="category.icon" size="32" color="white" />
            </div>
            <NH3 class="category-title">{{ dt(category.title) }}</NH3>
            <NP class="category-description">{{ dt(category.description) }}</NP>
            <div class="category-meta">
              <span class="article-count">{{ category.articles }} {{ dt('help.articles') }}</span>
            </div>
          </div>
        </NGridItem>
      </NGrid>
    </div>

    <!-- Popular Articles -->
    <div class="popular-section">
      <NH2 class="section-title">{{ dt('help.popularTitle') }}</NH2>
      <div class="articles-grid">
        <div v-for="(article, index) in popularArticles" :key="index" class="article-card">
          <div class="article-header">
            <BookOpen size="20" color="#36d1dc" />
            <span class="article-category">{{ article.category }}</span>
          </div>
          <NH3 class="article-title">{{ dt(article.title) }}</NH3>
          <div class="article-meta">
            <Clock size="16" />
            <span>{{ article.readTime }} {{ dt('help.minRead') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- FAQ Section -->
    <div class="faq-section">
      <NH2 class="section-title">{{ dt('help.faqTitle') }}</NH2>
      <NTabs type="line" animated>
        <NTabPane :name="'general'" :tab="dt('help.faq.tabs.general')">
          <NCollapse>
            <NCollapseItem
              v-for="(faq, index) in faqByCategory.general"
              :key="index"
              :title="dt(faq.q)"
              :name="index"
            >
              <NP>{{ dt(faq.a) }}</NP>
            </NCollapseItem>
          </NCollapse>
        </NTabPane>
        
        <NTabPane :name="'lawyers'" :tab="dt('help.faq.tabs.lawyers')">
          <NCollapse>
            <NCollapseItem
              v-for="(faq, index) in faqByCategory.lawyers"
              :key="index"
              :title="dt(faq.q)"
              :name="index"
            >
              <NP>{{ dt(faq.a) }}</NP>
            </NCollapseItem>
          </NCollapse>
        </NTabPane>
        
        <NTabPane :name="'contact'" :tab="dt('help.faq.tabs.contact')">
          <NCollapse>
            <NCollapseItem
              v-for="(faq, index) in faqByCategory.contact"
              :key="index"
              :title="dt(faq.q)"
              :name="index"
            >
              <NP>{{ dt(faq.a) }}</NP>
            </NCollapseItem>
          </NCollapse>
        </NTabPane>
        
        <NTabPane :name="'technical'" :tab="dt('help.faq.tabs.technical')">
          <NCollapse>
            <NCollapseItem
              v-for="(faq, index) in faqByCategory.technical"
              :key="index"
              :title="dt(faq.q)"
              :name="index"
            >
              <NP>{{ dt(faq.a) }}</NP>
            </NCollapseItem>
          </NCollapse>
        </NTabPane>
      </NTabs>
    </div>

    <!-- Contact Support -->
    <div class="support-section">
      <NH2 class="section-title">{{ dt('help.supportTitle') }}</NH2>
      <NP class="support-subtitle">{{ dt('help.supportSubtitle') }}</NP>
      
      <NGrid :cols="3" :x-gap="20" responsive="screen" :collapsed="['xs', 's']">
        <NGridItem v-for="(option, index) in contactOptions" :key="index">
          <div class="contact-option-card">
            <div class="option-icon" :style="{ backgroundColor: option.color }">
              <component :is="option.icon" size="28" color="white" />
            </div>
            <NH3 class="option-title">{{ dt(option.title) }}</NH3>
            <NP class="option-description">{{ dt(option.description) }}</NP>
            <NButton type="primary" ghost class="option-button">
              {{ dt(option.action) }}
            </NButton>
          </div>
        </NGridItem>
      </NGrid>
    </div>

    <!-- Help Alert -->
    <NAlert type="info" class="help-alert">
      <template #icon>
        <HelpCircle />
      </template>
      {{ dt('help.bottomAlert') }}
    </NAlert>
  </div>
</template>

<style scoped>
.help-container {
  padding: 16px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  color: #333;
  overflow-x: hidden;
  box-sizing: border-box;
}

.hero-section {
  text-align: center;
  padding: 40px 0;
}

.hero-subtitle {
  font-size: 1.2rem;
  margin-top: 12px;
  color: #555;
  margin-bottom: 32px;
}

.search-section {
  max-width: 600px;
  margin: 0 auto;
}

.search-input {
  margin-bottom: 16px;
}

.search-suggestions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.suggestion-label {
  color: #666;
  font-size: 0.9rem;
  margin-right: 8px;
}

.suggestion-tag {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.suggestion-tag:hover {
  transform: scale(1.05);
}

.section-title {
  text-align: center;
  margin-bottom: 30px;
  font-size: 1.5rem;
  position: relative;
}

.section-title::after {
  content: "";
  display: block;
  width: 50px;
  height: 3px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  margin: 10px auto 0;
  border-radius: 2px;
}

.categories-section {
  margin: 60px 0;
}

.category-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.category-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
}

.category-icon {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.category-title {
  margin-bottom: 12px;
  color: #333;
}

.category-description {
  color: #666;
  line-height: 1.6;
  flex: 1;
}

.category-meta {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.article-count {
  color: #36d1dc;
  font-weight: 500;
  font-size: 0.9rem;
}

.popular-section {
  margin: 60px 0;
}

.articles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.article-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  cursor: pointer;
}

.article-card:hover {
  transform: translateY(-3px);
}

.article-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.article-category {
  color: #36d1dc;
  font-size: 0.9rem;
  font-weight: 500;
}

.article-title {
  margin-bottom: 12px;
  color: #333;
  font-size: 1.1rem;
}

.article-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #666;
  font-size: 0.9rem;
}

.faq-section {
  margin: 60px 0;
}

.support-section {
  margin: 60px 0;
}

.support-subtitle {
  text-align: center;
  color: #666;
  margin-bottom: 40px;
  font-size: 1.1rem;
}

.contact-option-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.contact-option-card:hover {
  transform: translateY(-3px);
}

.option-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.option-title {
  margin-bottom: 12px;
  color: #333;
}

.option-description {
  color: #666;
  line-height: 1.6;
  flex: 1;
  margin-bottom: 20px;
}

.option-button {
  align-self: center;
}

.help-alert {
  margin: 40px 0;
  border-radius: 12px;
}

:deep(.n-collapse-item__header) {
  font-weight: 500;
}

:deep(.n-tabs-nav) {
  margin-bottom: 24px;
}

@media screen and (max-width: 768px) {
  .search-suggestions {
    justify-content: center;
  }
  
  .suggestion-label {
    width: 100%;
    text-align: center;
    margin-bottom: 8px;
    margin-right: 0;
  }
  
  .articles-grid {
    grid-template-columns: 1fr;
  }
  
  .category-card,
  .contact-option-card {
    margin-bottom: 16px;
  }
}
</style>